import React, { useId, useState } from 'react';
import { useIdentity } from '../../hooks/useIdentity';
import { AnimalAvatar, BubblesBg, Button, Icon } from '../../components';
import OnboardingPitch from './OnboardingPitch';
import './OnboardingScreen.css';

const MAX_HANDLE_LENGTH = 15;

/**
 * Glacier onboarding screen. Replaces the legacy CreateUser.
 *
 * Responsive: a single mobile-first centered card on <900px, expanding
 * to a two-column layout (left pitch / right card) at >=900px. Breakpoint
 * is handled entirely in CSS — no JS viewport detection — so initial
 * render is stable across SSR/CSR and resize is free.
 *
 * Both BubblesBg densities are rendered; only one is visible at a time
 * via the `data-density` attribute + matching media queries in the CSS.
 * This keeps the component declarative and avoids matchMedia plumbing.
 *
 * Submission funnels through useIdentity().setHandle — the only side
 * effect. App routing flips to chat on the next render because the
 * provider exposes hasHandle = !!handle.
 */
const OnboardingScreen = () => {
  const { setHandle, avatarSeed, rerollAvatar } = useIdentity();
  const [value, setValue] = useState('');
  const inputId = useId();

  const trimmed = value.trim();
  const canSubmit = trimmed.length > 0;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setHandle(trimmed);
  };

  const handleReroll = () => {
    rerollAvatar();
  };

  return (
    <div className="onboarding-screen" data-onboarding-root>
      <BubblesBg density="mobile" />
      <BubblesBg density="desktop" />

      <main
        id="onboarding-main"
        className="onboarding-screen__main"
        role="main"
      >
        <div className="onboarding-screen__layout">
          <OnboardingPitch />

          <form
            className="onboard-card onboarding-card"
            onSubmit={handleSubmit}
            aria-labelledby="onboarding-card-heading"
            noValidate
          >
            <div className="avatar-preview">
              <div className="swatch" data-testid="onboarding-avatar">
                <AnimalAvatar
                  key={avatarSeed}
                  seed={avatarSeed}
                  size={56}
                  ring
                />
              </div>
              <button
                type="button"
                className="reroll"
                onClick={handleReroll}
                aria-label="Reroll avatar"
              >
                <Icon name="refresh" size={16} />
              </button>
            </div>

            <h1
              id="onboarding-card-heading"
              className="onboarding-card__heading onboarding-card__heading--mobile"
            >
              Let&apos;s get you a name
            </h1>
            <h2 className="onboarding-card__heading onboarding-card__heading--desktop">
              Drop into the conversation.
            </h2>

            <p className="onboarding-card__subhead">
              Anonymous. Ephemeral. No sign-up &mdash; just vibes.
            </p>

            <label htmlFor={inputId} className="onboarding-card__label">
              Pick a handle
            </label>
            <div className="input-shell">
              <input
                id={inputId}
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                maxLength={MAX_HANDLE_LENGTH}
                placeholder="pick a handle…"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                aria-describedby={`${inputId}-count`}
              />
              <span
                id={`${inputId}-count`}
                className="count"
                aria-live="polite"
                aria-atomic="true"
              >
                {value.length}/{MAX_HANDLE_LENGTH}
              </span>
            </div>

            <Button
              type="submit"
              variant="primary"
              disabled={!canSubmit}
              className="onboarding-card__cta"
            >
              Let&apos;s go
              <Icon name="sparkle" size={16} />
            </Button>

            <p className="onboarding-card__hint">
              You&apos;ll land in <b>#GLOB</b>
            </p>
          </form>
        </div>
      </main>
    </div>
  );
};

export default OnboardingScreen;
